import { useGamesData } from "entities/game/hooks/useGamesData";
import { Game } from "entities/game/types";
import { FC, useState, PropsWithChildren, useEffect } from "react";
import { useClearRefinements } from "react-instantsearch";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { createContext } from "shared/utils/context";
import { useMetadataTagsFilter } from "../hooks/useMetadataTagsFilter";
import { useServiceTagsFilter } from "../hooks/useServiceTagsFilter";
import { useStatusFilter } from "../hooks/useStatusFilter";
import { useThemeTagsFilter } from "../hooks/useThemeTagsFilter";
import { GamesPageType, SelectedFilterTags } from "./types";

const [Provider, useGamesPage] = createContext<GamesPageType>({
  name: "GamesPageContext",
  hookName: "useGamesPage",
  providerName: "GamesPageProvider",
});

const GamesPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const gameDetailsDrawer = useDrawerState<Game>();
  const filterDrawerDrawer = useDrawerState<string>();

  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const [searchFilterValue, setSearchFilterValue] = useState("");
  const [selectedFilterValues, setSelectedFilterValues] =
    useState<SelectedFilterTags>({});

  const { serviceTagsFilterGames, ...serviceTagFilterApi } =
    useServiceTagsFilter();

  const { themeTagsFilterGames, ...themeTagFilterApi } = useThemeTagsFilter();

  const { metadataTagsFilterGames, ...metadataTagFilterApi } =
    useMetadataTagsFilter();

  const {
    filteredValues: status,
    filterData: statusFilterData,
    setFilteredValues: setStatus,
  } = useStatusFilter();

  const { refine } = useClearRefinements();

  const { searchData, isLoading, tableData } = useGamesData({
    filterTags: [
      serviceTagsFilterGames,
      themeTagsFilterGames,
      metadataTagsFilterGames,
    ],
    isActive: status,
  });

  const onClearFilters = () => {
    serviceTagFilterApi.handleClearSelectedOptions();
    themeTagFilterApi.handleClearSelectedOptions();
    metadataTagFilterApi.handleClearSelectedOptions();
    setSelectedFilterValues({});
    setSearchFilterValue("");
    setStatus([]);
    refine();
  };

  useEffect(() => {
    searchData.onChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = {
    gameDetailsDrawer,
    filterDrawerDrawer,
    selectedGameId,
    setSelectedGameId,
    onClearFilters,
    searchData,
    tableData,
    isLoadingGames: isLoading,
    setSelectedFilterValues,
    selectedFilterValues,
    searchFilterValue,
    setSearchFilterValue,
    serviceTagFilterApi,
    themeTagFilterApi,
    metadataTagFilterApi,
    statusFilterData,
    setStatus,
    status,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { GamesPageProvider, useGamesPage };
